import React, { useEffect, useMemo } from "react";
import { Modal, ModalBody } from "reactstrap";
import { LabelPrintingContainer, ItemContainer } from "./style";

/**
 * Same sheet geometry as panel labels opened from Order → Label with:
 * rows=8&cols=3&top_margin=11&left_margin=6&row_margin=0&col_margin=3&client_logo=none
 * (see fe/src/order/Order.js).
 *
 * Replace or override `getCustomLabelSheetCellContent()` to supply label components.
 * Entries are repeated in order until the sheet (rows × cols) is full.
 */
const LAYOUT = {
  rows: 8,
  cols: 3,
  top: 11,
  left: 6,
  row: 0,
  col: 3,
};

const SHEET_CELL_COUNT = LAYOUT.rows * LAYOUT.cols;

const sheetPadding = `${LAYOUT.top - LAYOUT.row / 2}mm ${
  LAYOUT.left - LAYOUT.col / 2
}mm`;

const cellHeightMm =
  (297 - LAYOUT.top * 2) / LAYOUT.rows -
  LAYOUT.row * (LAYOUT.rows - 1);

const cellWidthMm =
  (210 - LAYOUT.left * 2 - LAYOUT.col * (LAYOUT.cols - 1)) / LAYOUT.cols;

const cellMargin = `${LAYOUT.row / 2}mm ${LAYOUT.col / 2}mm`;

const STICKER_EDGE_INSET_MM = 3;
const CONTENT_BORDER_MM = 0.5;

function CustomLabelStickerCell({ children }) {
  return (
    <div
      className="d-flex align-items-center justify-content-center w-100 h-100"
      style={{
        padding: `${STICKER_EDGE_INSET_MM}mm`,
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  );
}

export function BoardMaterialLabel({ title, grade, laminate }) {
  return (
    <>
      <div>{title}</div>
      <div>{grade}</div>
      <div>{laminate}</div>
    </>
  );
}

export function PanelLabel({ label, name }) {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center fs-6">
      <div className='fw-bold fs-5'>{label}</div>
      <div>{name}</div>
    </div>
  );
}

/** @returns {React.ReactElement[]} One component per distinct label (cycled to fill the sheet). */
export function getCustomLabelSheetCellContent() {
  return [
    <PanelLabel label="L" name="Carcass Left" />,
    <PanelLabel label="R" name="Carcass Right" />,
    <PanelLabel label="T" name="Carcass Top" />,
    <PanelLabel label="B" name="Carcass Bottom" />,
    <PanelLabel label="BK" name="Carcass Backsheet" />,
    <PanelLabel label="FS" name="Fixed Shelf" />,
    // <BoardMaterialLabel
    //   key="eco-mr"
    //   title="Eco MR Plywood"
    //   grade="IS 303 Grade - 16mm"
    //   laminate="Beige Fabric 0.7mm"
    // />,
    // <BoardMaterialLabel
    //   key="eco-bwp"
    //   title="Eco BWP Plywood"
    //   grade="IS 710 Grade - 16mm"
    //   laminate="Beige Fabric 0.7mm"
    // />,
  ];
}

export function buildCustomLabelSheetCells(labelCells) {
  const templates = Array.isArray(labelCells) ? labelCells : [];
  if (templates.length === 0) {
    return Array.from({ length: SHEET_CELL_COUNT }, () => null);
  }

  return Array.from({ length: SHEET_CELL_COUNT }, (_, index) => {
    const template = templates[index % templates.length];
    return React.isValidElement(template)
      ? React.cloneElement(template, { key: `sheet-cell-${index}` })
      : template;
  });
}

const CustomLabelSheetPrint = ({
  cellContent = getCustomLabelSheetCellContent(),
}) => {
  const sheetCells = useMemo(
    () => buildCustomLabelSheetCells(cellContent),
    [cellContent]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") window.history.back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Modal
      isOpen
      fullscreen
      fade={false}
      className="label-print-modal"
      contentClassName="border-0 shadow-none"
    >
      <ModalBody className="pt-0 px-0 px-md-5 label-print-modal-body">
        <div
          id="printable-div"
          className="printable-div price-print-view"
        >
          <LabelPrintingContainer
            style={{
              padding: sheetPadding,
            }}
          >
            {sheetCells.map((content, cellIdx) => (
              <ItemContainer
                key={`custom-label-${cellIdx}`}
                className="d-flex align-items-center justify-content-center"
                style={{
                  height: `${cellHeightMm}mm`,
                  width: `${cellWidthMm}mm`,
                  margin: cellMargin,
                }}
              >
                <CustomLabelStickerCell>{content}</CustomLabelStickerCell>
              </ItemContainer>
            ))}
          </LabelPrintingContainer>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CustomLabelSheetPrint;
